/**
 * Auth Token IndexedDB Backup
 *
 * Safari iPadOS aggressively purges localStorage (ITP - Intelligent Tracking
 * Prevention) but is far less aggressive with IndexedDB. This module mirrors
 * any auth-related localStorage writes into an IndexedDB object store so that
 * if Safari purges localStorage between sessions, we can transparently restore
 * the auth state on the next app boot and avoid forcing the user to log in
 * again.
 *
 * Design notes:
 *  - localStorage remains the primary store, because the auth seam
 *    (getAccessToken / isAuthenticated in authSession.ts) must be synchronous.
 *  - What gets mirrored: the Better Auth session token and the cached user
 *    (see authStorage.ts). The session token is the 60-day credential that
 *    everything else is minted from, so it is the one that must survive.
 *  - IndexedDB is a fire-and-forget mirror. It must NEVER block or throw into
 *    auth code paths.
 *  - On boot, restoreFromIndexedDB() copies any IDB entries whose keys are
 *    missing from localStorage back into localStorage.
 */

const DB_NAME = 'moly_auth_backup'
const STORE_NAME = 'auth_tokens'
const DB_VERSION = 1

let dbPromise: Promise<IDBDatabase | null> | null = null

function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null
  } catch {
    return false
  }
}

function openDB(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise

  if (!isIndexedDBAvailable()) {
    dbPromise = Promise.resolve(null)
    return dbPromise
  }

  dbPromise = new Promise((resolve) => {
    let settled = false
    const finish = (db: IDBDatabase | null) => {
      if (settled) return
      settled = true
      resolve(db)
    }

    // indexedDB.open() can stall indefinitely without firing any event (e.g. a
    // second tab racing a versionchange, or storage-layer contention). Cap it so
    // callers — most importantly the boot-time restore that gates the React
    // mount — never wait forever. On timeout, drop the cached promise so a later
    // call can retry once the contention clears.
    const timer = setTimeout(() => {
      dbPromise = null
      finish(null)
    }, 2000)

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME)
        }
      }

      request.onsuccess = () => {
        clearTimeout(timer)
        if (settled) {
          // Open resolved after the timeout already returned null; don't leak
          // the connection handle.
          try {
            request.result.close()
          } catch {
            // ignore
          }
          return
        }
        finish(request.result)
      }

      request.onerror = () => {
        clearTimeout(timer)
        finish(null)
      }

      request.onblocked = () => {
        clearTimeout(timer)
        finish(null)
      }
    } catch {
      clearTimeout(timer)
      finish(null)
    }
  })

  return dbPromise
}

function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | null,
): Promise<T | null> {
  return openDB().then((db) => {
    if (!db) return null
    return new Promise<T | null>((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, mode)
        const store = tx.objectStore(STORE_NAME)
        const request = fn(store)
        if (!request) {
          tx.oncomplete = () => resolve(null)
          tx.onerror = () => resolve(null)
          tx.onabort = () => resolve(null)
          return
        }
        request.onsuccess = () => resolve(request.result ?? null)
        request.onerror = () => resolve(null)
        tx.onerror = () => resolve(null)
        tx.onabort = () => resolve(null)
      } catch {
        resolve(null)
      }
    })
  })
}

/**
 * Mirror a key/value pair into IndexedDB. Fire-and-forget; failures are
 * silently ignored because this is a backup, never blocking.
 */
export function mirrorToIndexedDB(key: string, value: string): void {
  void withStore('readwrite', (store) => store.put(value, key)).catch(() => {})
}

/**
 * Remove a key from the IndexedDB backup. Fire-and-forget.
 */
export function removeFromIndexedDB(key: string): void {
  void withStore('readwrite', (store) => store.delete(key)).catch(() => {})
}

/**
 * On app boot, restore any localStorage keys that are missing but present in
 * IndexedDB. Resolves when done. Never throws.
 */
export async function restoreFromIndexedDB(): Promise<void> {
  try {
    const db = await openDB()
    if (!db) return

    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readonly')
        const store = tx.objectStore(STORE_NAME)
        const keysReq = store.getAllKeys()
        const valuesReq = store.getAll()

        let keys: Array<IDBValidKey> = []
        let values: Array<unknown> = []

        keysReq.onsuccess = () => {
          keys = keysReq.result
        }
        valuesReq.onsuccess = () => {
          values = valuesReq.result
        }

        tx.oncomplete = () => {
          try {
            for (let i = 0; i < keys.length; i++) {
              const key = keys[i]
              const value = values[i]
              if (typeof key !== 'string' || typeof value !== 'string') continue
              if (window.localStorage.getItem(key) === null) {
                window.localStorage.setItem(key, value)
              }
            }
          } catch {
            // ignore
          }
          resolve()
        }
        tx.onerror = () => resolve()
        tx.onabort = () => resolve()
      } catch {
        resolve()
      }
    })
  } catch {
    // Never block boot on backup restore failures.
  }
}

/**
 * Remove every backed-up key that matches `predicate`. Used to drop legacy
 * (Cognito / oidc-client-ts) entries so restoreFromIndexedDB() can't bring
 * them back. Resolves when done. Never throws.
 */
export async function removeFromIndexedDBWhere(
  predicate: (key: string) => boolean,
): Promise<void> {
  try {
    const db = await openDB()
    if (!db) return
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const store = tx.objectStore(STORE_NAME)
        const keysReq = store.getAllKeys()
        keysReq.onsuccess = () => {
          for (const key of keysReq.result) {
            if (typeof key === 'string' && predicate(key)) store.delete(key)
          }
        }
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
        tx.onabort = () => resolve()
      } catch {
        resolve()
      }
    })
  } catch {
    // ignore
  }
}

/**
 * Clear the entire IndexedDB backup (used on logout).
 */
export async function clearBackup(): Promise<void> {
  try {
    const db = await openDB()
    if (!db) return
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const store = tx.objectStore(STORE_NAME)
        store.clear()
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
        tx.onabort = () => resolve()
      } catch {
        resolve()
      }
    })
  } catch {
    // ignore
  }
}
